import BannerService from '@engoo/banner-service';
import { BannerProvider } from '@engoo/react-banner';
import ServerTimeProvider from '@engoo/react-server-time';
import ServerTime from '@engoo/server-time';
import TranscoderService from '@engoo/transcoder-service';
import GlobalCall from 'app/components/call/GlobalCall';
import AnchorScrollingContextProvider from 'app/components/common/AnchorScrollingContext';
import { LazyCacheProvider } from 'app/components/common/Lazy';
import { UniqueIdProvider } from 'app/components/common/UniqueIdRoot';
import SetWindowViewport from 'app/components/layout/SetWindowViewport';
import { I18n } from 'app/components/localization';
import { WithModalCoordinator } from 'app/components/modals/Modal';
import { ActiveRouteProvider, GetActiveRoute } from 'app/components/router/ActiveRoute';
import { SetHistory } from 'app/components/router/History';
import WindowScrollProvider from 'app/components/scroll/WindowScrollProvider';
import FluentdContextUpdater from 'app/components/utilities/FluentdContextUpdater';
import { WebAudioProvider } from 'app/components/WebAudioContext';
import ServerCheck from 'app/constants/enums/ServerCheck';
import SyncNavigator from 'app/containers/call/SyncNavigator';
import LocaleWrapper from 'app/containers/i18n/LocaleWrapper';
import { AllRtcProviders } from 'app/containers/rtc';
import { BannerServiceProvider } from 'app/services/bannerService/react';
import { getGlobalHistory } from 'app/services/history';
import { LocalizationService } from 'app/services/localization';
import { TranscoderServiceProvider } from 'app/services/transcoderService/react';
import { globalUnique } from 'app/services/Unique';
import { componentType, historyShape } from 'app/utils/propTypes';
import { LocalStorageProvider, SessionStorageProvider } from 'app/utils/StorageContext';
import PropTypes from 'prop-types';
import React from 'react';
import { Provider } from 'react-redux';
import HandleRoute, { NotFound, OrganizationConfiguredRoutesProvider } from 'app/components/HandleRoute';
import { AuthUserProvider } from './auth/AuthUserContext';
import { ModalCoordsProvider } from 'app/components/modals/ModalCoordsContext';
import { ProvideCookieInteractions } from 'app/services/cookies';
import { RecycledComponentsProvider } from 'app/components/utilities/RecycledComponentsProvider';
import { BaseBrandProvider } from './OrganizationContext';
import { Brand } from 'app/constants/schemas';
import { PreservingSearchParamsProvider } from 'app/containers/search/PreservingSearchParams';
import { SelectedRouteUpdater } from 'app/components/utilities/SelectedRoute';
import AnalyticsServiceConfigurator from 'app/components/analytics/AnalyticsServiceConfigurator';
import DiscreetErrorBoundary from './utilities/DiscreetErrorBoundary';

const AppMiddleware = ({
  store,
  history = getGlobalHistory(),
  brand,
  localizationService,
  bannerService,
  cookieInterface,
  children,
  serverTime,
  transcoderService,
  localStorageStore,
  sessionStorageStore,
}) => (
  <LazyCacheProvider>
    <SessionStorageProvider store={sessionStorageStore}>
      <LocalStorageProvider store={localStorageStore}>
        <ServerTimeProvider serverTime={serverTime}>
          <WebAudioProvider>
            <TranscoderServiceProvider transcoderService={transcoderService}>
              <LocaleWrapper localizationService={localizationService}>
                <I18n>{({ locale }) => (
                  <ProvideCookieInteractions value={cookieInterface}>
                    <BannerProvider
                      bannerService={bannerService}
                      locale={locale}
                      transcoderService={transcoderService}
                    >
                      <BannerServiceProvider bannerService={bannerService}>
                        <SetWindowViewport>
                          <Provider store={store}>
                            <AuthUserProvider>
                              <BaseBrandProvider brand={brand}>
                                <OrganizationConfiguredRoutesProvider>
                                  <SetHistory value={history}>
                                    <ActiveRouteProvider>
                                      <FluentdContextUpdater />
                                      <AnalyticsServiceConfigurator />
                                      <SelectedRouteUpdater />
                                      <WithModalCoordinator>
                                        <PreservingSearchParamsProvider>
                                          <AllRtcProviders>
                                            <WindowScrollProvider>
                                              <AnchorScrollingContextProvider>
                                                <DiscreetErrorBoundary name="SyncNavigator">
                                                  <SyncNavigator />
                                                </DiscreetErrorBoundary>
                                                <UniqueIdProvider service={globalUnique}>
                                                  <ModalCoordsProvider>
                                                    <RecycledComponentsProvider>
                                                      <GlobalCall>
                                                        {children}
                                                      </GlobalCall>
                                                    </RecycledComponentsProvider>
                                                  </ModalCoordsProvider>
                                                </UniqueIdProvider>
                                              </AnchorScrollingContextProvider>
                                            </WindowScrollProvider>
                                          </AllRtcProviders>
                                        </PreservingSearchParamsProvider>
                                      </WithModalCoordinator>
                                    </ActiveRouteProvider>
                                  </SetHistory>
                                </OrganizationConfiguredRoutesProvider>
                              </BaseBrandProvider>
                            </AuthUserProvider>
                          </Provider>
                        </SetWindowViewport>
                      </BannerServiceProvider>
                    </BannerProvider>
                  </ProvideCookieInteractions>
                )}</I18n>
              </LocaleWrapper>
            </TranscoderServiceProvider>
          </WebAudioProvider>
        </ServerTimeProvider>
      </LocalStorageProvider>
    </SessionStorageProvider>
  </LazyCacheProvider>
);

const App = ({
  fallback = NotFound,
  ...rest
}) => (
  <ServerCheck>
    <AppMiddleware
      {...rest}
    >
      <GetActiveRoute>{({ route, params, query }) =>
        <HandleRoute route={route} params={params} fallback={fallback} />
      }</GetActiveRoute>
    </AppMiddleware>
  </ServerCheck>
);

const storeProp = PropTypes.shape({ getState: PropTypes.func }).isRequired;

AppMiddleware.propTypes = {
  bannerService: PropTypes.instanceOf(BannerService).isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  children: PropTypes.node,
  history: historyShape,
  cookieInterface: PropTypes.object,
  localizationService: PropTypes.instanceOf(LocalizationService).isRequired,
  serverTime: PropTypes.instanceOf(ServerTime),
  store: storeProp,
  transcoderService: PropTypes.instanceOf(TranscoderService).isRequired,
  localStorageStore: PropTypes.object.isRequired,
  sessionStorageStore: PropTypes.object.isRequired,
};

App.propTypes = {
  bannerService: PropTypes.instanceOf(BannerService).isRequired,
  fallback: componentType,
  history: historyShape,
  cookieInterface: PropTypes.object,
  brand: PropTypes.instanceOf(Brand).isRequired,
  localizationService: PropTypes.instanceOf(LocalizationService).isRequired,
  serverTime: PropTypes.instanceOf(ServerTime),
  store: storeProp,
  transcoderService: PropTypes.instanceOf(TranscoderService).isRequired,
  localStorageStore: PropTypes.object.isRequired,
  sessionStorageStore: PropTypes.object.isRequired,
};

export default App;
